import { ToastAction } from "@/components/ui/toast";
import { isThrottleError } from "@/lib/errors";
import type { QueryKey } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import useFetchErrorNotifier from "./useFetchErrorNotifier";

type FetchErrorToastOptions = {
    queryKey?: QueryKey;
    titleTranslationKey: string;
    onRetry?: () => void;
};

// Graph 429 throttling is transient: show a soft toast with a Retry action
// instead of the red "contact support" toast kept for real errors.
const useFetchErrorToast = () => {
    const { t } = useTranslation();
    const notifyFetchError = useFetchErrorNotifier();

    return (
        error: unknown,
        { queryKey, titleTranslationKey, onRetry }: FetchErrorToastOptions,
    ) => {
        if (isThrottleError(error)) {
            const retryLabel = t("errors.throttle.retry");

            notifyFetchError({
                queryKey,
                titleTranslationKey: "errors.throttle.title",
                descriptionTranslationKey: "errors.throttle.description",
                variant: "default",
                action: onRetry ? (
                    <ToastAction altText={retryLabel} onClick={onRetry}>
                        {retryLabel}
                    </ToastAction>
                ) : undefined,
            });

            return;
        }

        notifyFetchError({ queryKey, titleTranslationKey });
    };
};

export default useFetchErrorToast;
